// ============================================================================
// ThreatScoreGauge — SoulSearch Phase B
// ============================================================================
// SVG semi-circle gauge untuk threat score 0-100.
// Animated needle + color zones.
// Props: score (0-100), category, factors[], lang
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../../contexts/AppContext';

// SVG attribute (stroke/fill) tidak resolve CSS var reliably across browsers.
// Pakai theme-aware PALETTES dict supaya light mode juga adapt (sama pattern
// dengan NetworkGraph canvas).
const PALETTES = {
  dark:  { green: '#00ff88', yellow: '#ffd60a', orange: '#ff6b2b', red: '#ff2d55' },
  light: { green: '#008844', yellow: '#b89400', orange: '#c54a00', red: '#d12440' },
};

function buildZones(p) {
  return [
    { min: 0,  max: 30, color: p.green,  label_id: 'AMAN',    label_en: 'CLEAR'   },
    { min: 30, max: 60, color: p.yellow, label_id: 'PANTAU',  label_en: 'WATCH'   },
    { min: 60, max: 80, color: p.orange, label_id: 'CURIGA',  label_en: 'SUSPECT' },
    { min: 80, max:100, color: p.red,    label_id: 'WANTED',  label_en: 'WANTED'  },
  ];
}

function getZone(zones, score) {
  return zones.find(z => score >= z.min && score <= z.max) || zones[0];
}

// Konversi score 0-100 ke sudut needle (-130° sampai +130°)
function scoreToAngle(score) {
  return -130 + (score / 100) * 260;
}

export default function ThreatScoreGauge({ score = 0, factors = [], lang = 'id', animated = true }) {
  const [displayScore, setDisplayScore] = useState(animated ? 0 : score);
  const rafRef = useRef(null);
  const { theme } = useApp();
  const palette = PALETTES[theme === 'light' ? 'light' : 'dark'];
  const ZONES = buildZones(palette);

  useEffect(() => {
    if (!animated) { setDisplayScore(score); return; }
    let start = null;
    const duration = 1200; // ms
    const from = displayScore;
    const to = score;
    const animate = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3); // cubic ease-out
      setDisplayScore(Math.round(from + (to - from) * ease));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [score]);

  const zone = getZone(ZONES, displayScore);
  const angle = scoreToAngle(displayScore);
  const cx = 100, cy = 90, r = 70;

  // Needle endpoint
  const rad = (angle - 90) * (Math.PI / 180);
  const nx = cx + r * 0.75 * Math.cos(rad);
  const ny = cy + r * 0.75 * Math.sin(rad);

  // Arc paths untuk zones (semi-circle dari -130° a +130°)
  function arcPath(startAngle, endAngle, color) {
    const startRad = (startAngle - 90) * Math.PI / 180;
    const endRad   = (endAngle   - 90) * Math.PI / 180;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    const large = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  }

  return (
    <div style={{ textAlign: 'center' }}>
      <svg viewBox="0 0 200 130" width="200" height="130" aria-label={`Threat Score ${displayScore}`}>
        {/* Background track */}
        <path
          d={arcPath(-130, 130, '#333')}
          fill="none" stroke="var(--border-dim)" strokeWidth="10" strokeLinecap="round"
        />
        {/* Zone arcs */}
        {ZONES.map((z, i) => {
          const zStart = -130 + (z.min / 100) * 260;
          const zEnd   = -130 + (z.max / 100) * 260;
          return (
            <path key={i}
              d={arcPath(zStart, zEnd, z.color)}
              fill="none" stroke={z.color + '44'} strokeWidth="10"
            />
          );
        })}
        {/* Filled progress arc */}
        {displayScore > 0 && (
          <path
            d={arcPath(-130, -130 + (displayScore / 100) * 260, zone.color)}
            fill="none" stroke={zone.color} strokeWidth="10" strokeLinecap="round"
          />
        )}
        {/* Center dot */}
        <circle cx={cx} cy={cy} r={5} fill={zone.color} />
        {/* Needle */}
        <line x1={cx} y1={cy} x2={nx} y2={ny}
          stroke={zone.color} strokeWidth="2.5" strokeLinecap="round" />
        {/* Score text */}
        <text x={cx} y={cy - 12} textAnchor="middle"
          fontSize="28" fontFamily="IBM Plex Mono" fontWeight="900"
          fill={zone.color}>
          {displayScore}
        </text>
        <text x={cx} y={cy + 4} textAnchor="middle"
          fontSize="8" fontFamily="IBM Plex Mono" fontWeight="700"
          fill={zone.color} letterSpacing="2">
          {lang === 'id' ? zone.label_id : zone.label_en}
        </text>
        {/* Min/Max labels */}
        <text x="28" y="118" fontSize="8" fontFamily="IBM Plex Mono" fill="var(--text-mute)">0</text>
        <text x="165" y="118" fontSize="8" fontFamily="IBM Plex Mono" fill="var(--text-mute)">100</text>
      </svg>

      {/* Factors breakdown */}
      {factors.length > 0 && (
        <div style={{ textAlign: 'left', marginTop: 8 }}>
          <p style={{ fontSize: 8, fontFamily: 'IBM Plex Mono', color: 'var(--text-mute)',
                      textTransform: 'uppercase', letterSpacing: 2, marginBottom: 4 }}>
            {lang === 'id' ? 'Faktor risiko' : 'Risk factors'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {factors.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 9, color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono' }}>
                  {f.description || f.factor_name}
                </span>
                <span style={{
                  fontSize: 9, fontFamily: 'IBM Plex Mono', fontWeight: 700,
                  color: f.weighted_score >= 30 ? 'var(--color-red)' : f.weighted_score >= 15 ? 'var(--color-yellow)' : 'var(--text-mute)',
                }}>
                  +{f.weighted_score}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
